import { Fragment, useState } from 'react';
import { api } from '../lib/api';
import type { AuditLog } from '../lib/api';
import { presentAuditEvent } from '../lib/auditPresenter';
import { Empty, ErrorNotice, Loading, Panel, formatDateTime, useAsync } from '../components/ui';

export default function AuditLogPage() {
  const [action, setAction] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const { data, error, loading } = useAsync(
    (signal) =>
      api.get<{ logs: AuditLog[]; total: number }>(
        `/audit-logs?limit=${limit}&offset=${offset}${action ? `&action=${encodeURIComponent(action)}` : ''}`,
        signal,
      ),
    [action, offset],
  );

  /**
   * Filterable actions, grouped the way an administrator investigates.
   *
   * This list had fallen behind the backend: the per-control security events,
   * the policy outcome events and the wipe events were all being written and
   * rendered, but could not be filtered to — which is exactly when you need a
   * filter. Kept in step with AuditAction in backend/src/services/audit.
   */
  const actionGroups: Array<{ label: string; actions: string[] }> = [
    {
      label: 'Authentication',
      actions: ['admin.login', 'admin.login_failed', 'admin.logout'],
    },
    {
      label: 'Enrollment',
      actions: [
        'enrollment.token_generated', 'enrollment.token_revoked', 'enrollment.token_expired',
        'device.enrolled', 'device.enrollment_failed', 'device.reenrolled',
        'device.identity_changed', 'device.unenrolled',
      ],
    },
    {
      label: 'Policy',
      actions: [
        'policy.created', 'policy.updated', 'policy.deleted', 'policy.validated',
        'policy.rejected_unsafe', 'policy.delivered', 'policy.applied',
        'policy.partially_applied', 'policy.failed', 'device.policy_changed',
      ],
    },
    {
      label: 'Applications',
      actions: [
        'apk.uploaded', 'apk.upload_rejected', 'apk.deleted',
        'deployment.started', 'deployment.succeeded', 'deployment.failed', 'deployment.cancelled',
        'application.uninstall_requested', 'application.data_cleared',
        'application.signature_mismatch',
      ],
    },
    {
      label: 'Security controls',
      actions: [
        'security.kiosk_control', 'security.camera_restriction',
        'security.screen_capture_restriction', 'security.usb_restriction',
        'security.debugging_restriction', 'security.factory_reset_restriction',
        'security.file_manager_restriction', 'security.failed_password_wipe_threshold',
        'security.frp',
      ],
    },
    {
      label: 'Device actions',
      actions: [
        'device.locked', 'device.rebooted', 'device.renamed',
        'device.kiosk_started', 'device.kiosk_stopped',
        'device.wipe_requested', 'device.wipe_refused', 'device.wiped',
      ],
    },
    // Connectivity conditions are NOT listed here. They are alerts - things the
    // server observed - and they live in the Alerts feed, not the audit log.
    // Offering them as audit filters implied the audit log was a notification
    // feed, which is exactly the confusion this separation removes.
    {
      label: 'Student assignment',
      actions: [
        'student.assignment_created', 'student.assignment_released',
        'student.assignment_changed', 'student.assignment_refused',
      ],
    },
    {
      label: 'Settings',
      actions: ['settings.alerts_enabled', 'settings.alerts_disabled'],
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Audit log</h2>
          <p>Every administrative action, with no secrets recorded.</p>
        </div>
      </div>

      <ErrorNotice error={error} />

      <Panel>
        <label style={{ maxWidth: 340, marginBottom: 0 }}>
          <span>Filter by action</span>
          <select value={action} onChange={(e) => { setAction(e.target.value); setOffset(0); }}>
            <option value="">All actions</option>
            {actionGroups.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.actions.map((a) => <option key={a} value={a}>{a}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
      </Panel>

      <Panel>
        {loading && !data ? (
          <Loading what="audit log" />
        ) : (data?.logs.length ?? 0) === 0 ? (
          <Empty>No matching entries.</Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th><th>Actor</th><th>Action</th>
                    <th>Target</th><th>Result</th><th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.logs.map((log) => {
                    const event = presentAuditEvent(log);
                    return (
                      <tr key={log.id}>
                        <td className="small muted" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.created_at)}</td>
                        <td className="small">
                          {log.actor_label ?? '—'}
                          <div className="muted">{log.actor_type}</div>
                        </td>
                        <td className="small">
                          <strong>{event.title}</strong>
                          <div className="muted mono" style={{ fontSize: '0.85em' }}>{log.action}</div>
                        </td>
                        <td className="small">
                          {log.target_label ?? '—'}
                          {log.target_type && <div className="muted">{log.target_type}</div>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {log.success
                            ? <span className="badge ok">OK</span>
                            : <span className="badge danger">FAILED</span>}
                          {event.severity !== 'info' && (
                            <div
                              className="badge"
                              style={{ marginTop: 4, textTransform: 'uppercase' }}
                              data-severity={event.severity}
                            >
                              {event.severity}
                            </div>
                          )}
                        </td>
                        {/* Structured data stays in the database; this column
                            renders it, rather than dumping JSON at the reader. */}
                        <td className="small" style={{ maxWidth: 420 }}>
                          <div>{event.summary}</div>
                          {event.fields.length > 0 && (
                            <dl className="audit-fields">
                              {event.fields.map((f) => (
                                <Fragment key={f.label}>
                                  <dt className="muted">{f.label}</dt>
                                  <dd>{f.value}</dd>
                                </Fragment>
                              ))}
                            </dl>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="actions" style={{ marginTop: 12 }}>
              <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
                Previous
              </button>
              <span className="small muted">
                {offset + 1}–{Math.min(offset + limit, data!.total)} of {data!.total}
              </span>
              <button disabled={offset + limit >= data!.total} onClick={() => setOffset(offset + limit)}>
                Next
              </button>
            </div>
          </>
        )}
        <p className="small muted" style={{ marginBottom: 0, marginTop: 12 }}>
          Metadata is scrubbed of passwords, tokens, keys and Wi-Fi credentials before it is
          written, so there is nothing sensitive to hide on the way out.
        </p>
      </Panel>
    </>
  );
}
